
import React, { useState, useEffect } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import {
    Card,
    CardBody,
    Col,
    Form,
    Input,
    Label,
    Row,
    FormFeedback,
    Spinner,
    Container,
    Button,
    CardHeader
} from "reactstrap";
import { Btn, H5 } from "../../AbstractElements";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiPost, apiGet, uploadFile } from "../../Utils";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Plus, Trash2 } from "react-feather";

interface FormValues {
    branch_id: string;
    vehicle_id: string;
    service_date: string;
    service_type: string;
    garage_name: string;
    odometer_reading: string;
    next_service_date: string;
    status: string;

    // Conditional
    total_cost?: string;
    invoice_reference?: string;
    invoice_document?: FileList;
    description?: string;

    parts_replaced: { name: string; amount: string }[];
}

const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

export default function AddMaintenance() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [branches, setBranches] = useState<any[]>([]);
    const [vehicles, setVehicles] = useState<any[]>([]);
    const [submissionBlocked, setSubmissionBlocked] = useState(false);
    const [pendingMessage, setPendingMessage] = useState("");

    const {
        control,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
        reset,
        register
    } = useForm<FormValues>({
        defaultValues: {
            parts_replaced: [{ name: "", amount: "" }]
        }
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "parts_replaced"
    });

    const selectedBranch = watch("branch_id");
    const selectedVehicle = watch("vehicle_id");
    const watchedStatus = watch("status");
    const watchedServiceDate = watch("service_date");
    const watchedNextServiceDate = watch("next_service_date");

    // Check for pending maintenance when vehicle changes
    useEffect(() => {
        if (!selectedVehicle) {
            setSubmissionBlocked(false);
            setPendingMessage("");
            return;
        }

        const checkPending = async () => {
            try {
                const response: any = await apiPost("maintenance/check_pending", { vehicle_id: selectedVehicle });
                if (response && response.exists) {
                    setPendingMessage(response.message);
                    setSubmissionBlocked(true);
                } else {
                    setSubmissionBlocked(false);
                    setPendingMessage("");
                }
            } catch (error) {
                console.error("Error checking pending maintenance", error);
                setSubmissionBlocked(false);
                setPendingMessage("");
            }
        };

        checkPending();
    }, [selectedVehicle]);

    // Fetch Branches on Mount
    useEffect(() => {
        const fetchBranches = async () => {
            try {
                const data: any = await apiPost("branches/list", { status: 'Active' });
                setBranches(data);
            } catch (error) {
                console.error(error);
            }
        };
        fetchBranches();
    }, []);

    // Fetch Vehicles when Branch changes
    useEffect(() => {
        const fetchVehicles = async () => {
            if (!selectedBranch) {
                setVehicles([]);
                return;
            }
            try {
                const response: any = await apiPost("vehicles/list", { branch_id: selectedBranch });
                if (Array.isArray(response)) {
                    const activeVehicles = response.filter((v: any) => v.status !== 'Sold');
                    setVehicles(activeVehicles);
                } else if (response.data) {
                    const activeVehicles = Array.isArray(response.data) ? response.data.filter((v: any) => v.status !== 'Sold') : [];
                    setVehicles(activeVehicles);
                } else {
                    setVehicles([]);
                }
            } catch (error) {
                console.error(error);
                setVehicles([]);
            }
        };

        fetchVehicles();
        setValue("vehicle_id", "");
    }, [selectedBranch, setValue]);

    const onSubmit = async (data: FormValues) => {
        if (submissionBlocked) {
            toast.error(pendingMessage || "Cannot submit: A pending maintenance record already exists for this vehicle.");
            return;
        }
        setLoading(true);
        try {
            let invoiceDocName = "";
            if (data.invoice_document && data.invoice_document.length > 0) {
                const file = data.invoice_document[0];
                if (file.size > 2 * 1024 * 1024) {
                    toast.error("File size must be less than 2MB");
                    setLoading(false);
                    return;
                }
                invoiceDocName = await uploadFile(file);
            }

            const parts = data.parts_replaced.filter(n => n.name.trim() !== "");

            const payload = {
                ...data,
                invoice_document: invoiceDocName,
                parts_replaced: parts
            };
            console.log(payload, "payload")
            await apiPost("maintenance/store", payload);
            toast.success("Maintenance record added successfully", { autoClose: 1000 });
            setTimeout(() => {
                navigate(`${process.env.PUBLIC_URL}/maintenance`);
            }, 1000);
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Error adding maintenance");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="page-body">
            <Breadcrumbs pageTitle="Add Maintenance" parent="Maintenance" title="Add Maintenance" />
            <Container fluid>
                <Card>
                    <CardBody>
                        <Form onSubmit={handleSubmit(onSubmit)}>
                            <H5>Maintenance Details</H5>
                            <hr />
                            <Row className="g-3">
                                <Col md={4}>
                                    <Label>Branch <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="branch_id"
                                        control={control}
                                        rules={{ required: "Branch is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input type="select" {...field} invalid={!!errors.branch_id}>
                                                    <option value="">Select Branch</option>
                                                    {branches.map((b: any) => (
                                                        <option key={b.id} value={b.id}>{b.branch_name}</option>
                                                    ))}
                                                </Input>
                                                <FormFeedback>{errors.branch_id?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Label>Vehicle <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="vehicle_id"
                                        control={control}
                                        rules={{ required: "Vehicle is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input type="select" {...field} invalid={!!errors.vehicle_id} disabled={!selectedBranch}>
                                                    <option value="">Select Vehicle</option>
                                                    {vehicles.map((v: any) => (
                                                        <option key={v.id} value={v.id}>{v.registration_number} ({v.brand} {v.model})</option>
                                                    ))}
                                                </Input>
                                                <FormFeedback>{errors.vehicle_id?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Label>Service Date <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="service_date"
                                        control={control}
                                        rules={{ required: "Service Date is required" }}
                                        render={({ field }) => (
                                            <>
                                                <DatePicker
                                                    className={`form-control ${errors.service_date ? "is-invalid" : ""}`}
                                                    selected={field.value ? new Date(field.value) : null}
                                                    onChange={(date) => {
                                                        const d = date ? formatDate(date) : "";
                                                        if (watchedNextServiceDate && d && new Date(d) > new Date(watchedNextServiceDate)) {
                                                            toast.error("Service date cannot be after Next Service date");
                                                            return;
                                                        }
                                                        field.onChange(d);
                                                    }}
                                                    placeholderText="YYYY-MM-DD"
                                                    showMonthDropdown
                                                    showYearDropdown
                                                    dropdownMode="select"
                                                />
                                                <FormFeedback className="d-block">{errors.service_date?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Label>Service Type <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="service_type"
                                        control={control}
                                        rules={{ required: "Service Type is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input {...field} placeholder="Oil Change, General Service..." invalid={!!errors.service_type} autoComplete="off" />
                                                <FormFeedback>{errors.service_type?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Label>Garage / Service Center <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="garage_name"
                                        control={control}
                                        rules={{ required: "Garage Name is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input {...field} placeholder="Garage Name" invalid={!!errors.garage_name} autoComplete="off" />
                                                <FormFeedback>{errors.garage_name?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Label>Odometer Reading (km) <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="odometer_reading"
                                        control={control}
                                        rules={{ required: "Odometer is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input type="number" {...field} placeholder="12000" invalid={!!errors.odometer_reading} autoComplete="off" />
                                                <FormFeedback>{errors.odometer_reading?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Label>Status <span className="text-danger">*</span></Label>
                                    <Controller
                                        name="status"
                                        control={control}
                                        rules={{ required: "Status is required" }}
                                        render={({ field }) => (
                                            <>
                                                <Input type="select" {...field} invalid={!!errors.status}>
                                                    <option value="">Select Status</option>
                                                    <option value="Pending">Pending</option>
                                                    <option value="In Progress">In Progress</option>
                                                    <option value="Completed">Completed</option>
                                                </Input>
                                                <FormFeedback>{errors.status?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>

                                <Col md={4}>
                                    <Label>Total Cost {watchedStatus === 'Completed' && <span className="text-danger">*</span>}</Label>
                                    <Controller
                                        name="total_cost"
                                        control={control}
                                        rules={{
                                            required: watchedStatus === 'Completed' ? "Total Cost is required for completed status" : false
                                        }}
                                        render={({ field }) => (
                                            <>
                                                <Input type="number" {...field} placeholder="0.00" invalid={!!errors.total_cost} autoComplete="off" />
                                                <FormFeedback>{errors.total_cost?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={4}>
                                    <Label>Invoice Reference {watchedStatus === 'Completed' && <span className="text-danger">*</span>}</Label>
                                    <Controller
                                        name="invoice_reference"
                                        control={control}
                                        rules={{
                                            required: watchedStatus === 'Completed' ? "Invoice Reference is required" : false
                                        }}
                                        render={({ field }) => (
                                            <>
                                                <Input {...field} placeholder="INV-001" invalid={!!errors.invoice_reference} autoComplete="off" />
                                                <FormFeedback>{errors.invoice_reference?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label>Next Service Due {watchedStatus === 'Completed' && <span className="text-danger">*</span>}</Label>
                                    <Controller
                                        name="next_service_date"
                                        control={control}
                                        rules={{
                                            required: watchedStatus === 'Completed' ? "Next Service Date (Due) is required" : false
                                        }}
                                        render={({ field }) => (
                                            <>
                                                <DatePicker
                                                    className={`form-control ${errors.next_service_date ? "is-invalid" : ""}`}
                                                    selected={field.value ? new Date(field.value) : null}
                                                    onChange={(date) => {
                                                        const d = date ? formatDate(date) : "";
                                                        if (watchedServiceDate && d && new Date(d) < new Date(watchedServiceDate)) {
                                                            toast.error("Next Service date cannot be before Service date");
                                                            return;
                                                        }
                                                        field.onChange(d);
                                                    }}
                                                    placeholderText="YYYY-MM-DD"
                                                    showMonthDropdown
                                                    showYearDropdown
                                                    dropdownMode="select"
                                                />
                                                <FormFeedback className="d-block">{errors.next_service_date?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                                <Col md={6}>
                                    <Label>Upload Invoice {watchedStatus === 'Completed' && <span className="text-danger">*</span>}</Label>
                                    <Controller
                                        name="invoice_document"
                                        control={control}
                                        rules={{
                                            required: watchedStatus === 'Completed' ? "Invoice Document is required" : false
                                        }}
                                        render={({ field: { value, onChange, ...field } }) => (
                                            <>
                                                <Input
                                                    type="file"
                                                    {...field}
                                                    accept="image/*,.pdf"
                                                    onChange={(e) => {
                                                        if (e.target.files && e.target.files.length > 0) {
                                                            const file = e.target.files[0];
                                                            if (file.size > 2 * 1024 * 1024) {
                                                                toast.error("File size must be less than 2MB");
                                                                e.target.value = ""; // Clear the input
                                                                onChange(null); // Clear form state
                                                                return;
                                                            }
                                                        }
                                                        onChange(e.target.files);
                                                    }}
                                                    invalid={!!errors.invoice_document}
                                                />
                                                <FormFeedback>{errors.invoice_document?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>

                                <Col md={12}>
                                    <Label>Description / Notes {watchedStatus === 'Completed' && <span className="text-danger">*</span>}</Label>
                                    <Controller
                                        name="description"
                                        control={control}
                                        rules={{
                                            required: watchedStatus === 'Completed' ? "Description is required" : false
                                        }}
                                        render={({ field }) => (
                                            <>
                                                <Input type="textarea" {...field} placeholder="Add notes..." invalid={!!errors.description} autoComplete="off" />
                                                <FormFeedback>{errors.description?.message}</FormFeedback>
                                            </>
                                        )}
                                    />
                                </Col>
                            </Row>

                            <Col xs={12} className="mt-4">
                                <Card className="mb-4" style={{ border: "1px solid #ced4da", borderRadius: "8px" }}>
                                    <CardHeader tag="h5" style={{
                                        backgroundColor: "#f8f9fa",
                                        borderBottom: "1px solid #ced4da", borderTopLeftRadius: "10px", borderTopRightRadius: "10px"
                                    }}>
                                        Parts Replaced
                                    </CardHeader>
                                    <CardBody>
                                        {fields.map((field, index) => {
                                            const { ref: nameRef, ...nameProps } = register(`parts_replaced.${index}.name`);
                                            const { ref: amountRef, ...amountProps } = register(`parts_replaced.${index}.amount`);
                                            return (
                                                <Row key={field.id} className="mb-2 align-items-center">
                                                    <Col md={6}>
                                                        <Input
                                                            {...nameProps}
                                                            innerRef={nameRef}
                                                            placeholder="Part Name"
                                                            autoComplete="off"
                                                        />
                                                    </Col>
                                                    <Col md={5}>
                                                        <Input
                                                            type="number"
                                                            {...amountProps}
                                                            innerRef={amountRef}
                                                            placeholder="Amount"
                                                            autoComplete="off"
                                                        />
                                                    </Col>
                                                    <Col md={1}>
                                                        <Button color="danger" size="sm" onClick={() => remove(index)}>
                                                            <Trash2 size={16} />
                                                        </Button>
                                                    </Col>
                                                </Row>
                                            );
                                        })}
                                        <Button color="success" size="sm" onClick={() => append({ name: "", amount: "" })} className="mt-2">
                                            <Plus size={16} /> Add Part
                                        </Button>
                                    </CardBody>
                                </Card>
                            </Col>

                            <div className="mt-4">
                                <Btn color="primary" disabled={loading} type="submit">
                                    {loading ? <> Submitting <Spinner size="sm" /> </> : "Submit"}
                                </Btn>
                            </div>
                        </Form>
                    </CardBody>
                </Card>
            </Container>
            <ToastContainer />
        </div>
    );
}
